import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { 
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { getRestaurants, deleteRestaurant, duplicateRestaurant } from '../lib/api';
import { toast } from 'sonner';
import { 
    Store, 
    Plus, 
    Search, 
    MoreVertical, 
    Edit, 
    Trash2, 
    Copy, 
    ExternalLink,
    TrendingUp 
} from 'lucide-react';

export default function Restaurants() {
    const [restaurants, setRestaurants] = useState([]);
    const [filteredRestaurants, setFilteredRestaurants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    const fetchRestaurants = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getRestaurants();
            setRestaurants(response.data);
            setFilteredRestaurants(response.data);
        } catch (err) {
            toast.error('Failed to load restaurants');
        } finally {
            setLoading(false);
        }
    }, []);

    // Re-fetch on every navigation (including back button)
    useEffect(() => {
        fetchRestaurants();
    }, [fetchRestaurants, location.key]);

    useEffect(() => {
        const filtered = restaurants.filter(r => 
            r.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setFilteredRestaurants(filtered);
    }, [searchQuery, restaurants]);

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await deleteRestaurant(deleteId);
            toast.success('Restaurant deleted');
            fetchRestaurants();
        } catch (err) {
            toast.error('Failed to delete restaurant');
        } finally {
            setDeleteId(null);
        }
    };

    const handleDuplicate = async (id) => {
        try {
            const response = await duplicateRestaurant(id);
            toast.success('Restaurant duplicated');
            navigate(`/dashboard/restaurants/${response.data.id}`);
        } catch (err) {
            toast.error('Failed to duplicate restaurant');
        }
    };

    const menuUrl = (slug) => `${window.location.origin}/menu/${slug}`;

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-semibold text-[#2B2A27] tracking-tight">Restaurants</h1>
                        <p className="text-[#69665B] mt-1">Manage your restaurant menus</p>
                    </div>
                    <Link to="/dashboard/restaurants/new">
                        <Button className="bg-[#C75D4E] hover:bg-[#A94E40] text-white rounded-full gap-2" data-testid="add-restaurant-btn">
                            <Plus className="w-4 h-4" />
                            Add Restaurant
                        </Button>
                    </Link>
                </div>

                {/* Search */}
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#969282]" />
                    <Input
                        placeholder="Search restaurants..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 border-[#E5E1D1] rounded-xl"
                        data-testid="search-restaurants-input"
                    />
                </div>

                {/* Restaurant Grid */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <Card key={i} className="border-[#E5E1D1]">
                                <CardContent className="p-6">
                                    <div className="h-32 bg-[#F0EDDF] rounded-xl animate-pulse" />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : filteredRestaurants.length === 0 ? (
                    <Card className="border-[#E5E1D1]">
                        <CardContent className="py-16 text-center">
                            <Store className="w-12 h-12 text-[#969282] mx-auto mb-4" />
                            <p className="text-[#69665B] mb-4">
                                {searchQuery ? 'No restaurants found' : 'No restaurants yet'}
                            </p>
                            {!searchQuery && (
                                <Link to="/dashboard/restaurants/new">
                                    <Button className="bg-[#C75D4E] hover:bg-[#A94E40] text-white rounded-full">
                                        Create your first restaurant
                                    </Button>
                                </Link>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredRestaurants.map((restaurant) => (
                            <Card 
                                key={restaurant.id} 
                                className="border-[#E5E1D1] card-hover overflow-hidden cursor-pointer"
                                data-testid={`restaurant-card-${restaurant.id}`}
                                onClick={() => navigate(`/dashboard/restaurants/${restaurant.id}`)}
                            >
                                <CardContent className="p-0">
                                    {/* Color Banner */}
                                    <div 
                                        className="h-24 flex items-center justify-center"
                                        style={{ backgroundColor: restaurant.primary_color || '#C75D4E' }}
                                    >
                                        {restaurant.logo_url ? (
                                            <img 
                                                src={restaurant.logo_url} 
                                                alt={restaurant.name}
                                                className="h-16 w-16 object-contain rounded-xl"
                                            />
                                        ) : (
                                            <Store className="w-10 h-10 text-white/80" />
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="p-5">
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <h3 className="font-semibold text-[#2B2A27] text-lg">{restaurant.name}</h3>
                                                <p className="text-sm text-[#969282]">/menu/{restaurant.slug}</p>
                                            </div>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon" 
                                                        className="text-[#69665B]" 
                                                        data-testid={`restaurant-menu-${restaurant.id}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <MoreVertical className="w-5 h-5" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/restaurants/${restaurant.id}`); }}>
                                                        <Edit className="w-4 h-4 mr-2" /> Edit
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicate(restaurant.id); }}>
                                                        <Copy className="w-4 h-4 mr-2" /> Duplicate
                                                    </DropdownMenuItem>
                                                    {restaurant.status === 'published' && (
                                                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); window.open(menuUrl(restaurant.slug), '_blank'); }}>
                                                            <ExternalLink className="w-4 h-4 mr-2" /> View Menu
                                                        </DropdownMenuItem>
                                                    )}
                                                    <DropdownMenuItem 
                                                        onClick={(e) => { e.stopPropagation(); setDeleteId(restaurant.id); }}
                                                        className="text-red-600 focus:text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                                                restaurant.status === 'published' ? 'badge-published' : 'badge-draft'
                                            }`}>
                                                {restaurant.status}
                                            </span>
                                            <div className="flex items-center gap-1 text-[#69665B] text-sm">
                                                <TrendingUp className="w-4 h-4" />
                                                {restaurant.views || 0} views
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Restaurant</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete the restaurant and all its categories, dishes, and data. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDelete}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="confirm-delete-btn"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </DashboardLayout>
    );
}
